import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { StepComponentProps } from "../../components/MultiStepGradeModal";
import { RealWorldConnectionCreateRequestDto } from "@packages/api/madlenAPI.schemas";

export const UsagePurposeStep: React.FC<
  StepComponentProps<RealWorldConnectionCreateRequestDto>
> = ({ data, onDataChange, themeColor }) => {
  const { t } = useTranslation();
  const [showCustomPurpose, setShowCustomPurpose] = useState(false);

  const usagePurposeOptions = [
    {
      id: "Öğrencinin Motivasyonunu Arttırmak",
      emoji: "🚀",
      label: t("realWorld.purpose.motivation"),
      desc: t("realWorld.purpose.motivationDesc"),
    },
    {
      id: "Öğrendiklerini Anlamlandırmasını Sağlamak",
      emoji: "💡",
      label: t("realWorld.purpose.understanding"),
      desc: t("realWorld.purpose.understandingDesc"),
    },
    {
      id: "Öğrencinin Kendi Deneyimleri İle İlişki Kurmasını Sağlamak",
      emoji: "🔗",
      label: t("realWorld.purpose.experience"),
      desc: t("realWorld.purpose.experienceDesc"),
    },
    {
      id: "Gerçek Hayatta Kullanmak",
      emoji: "🌍",
      label: t("realWorld.purpose.realLife"),
      desc: t("realWorld.purpose.realLifeDesc"),
    },
    {
      id: "Problem Çözme Becerisi Kazandırmak",
      emoji: "🧩",
      label: t("realWorld.purpose.problemSolving"),
      desc: t("realWorld.purpose.problemSolvingDesc"),
    },
    {
      id: "Eleştirel Düşünme Becerisi Kazandırmak",
      emoji: "🤔",
      label: t("realWorld.purpose.criticalThinking"),
      desc: t("realWorld.purpose.criticalThinkingDesc"),
    },
    {
      id: "Yerel ya da Küresel Güncel Konularla Bağ Kurmak",
      emoji: "📰",
      label: t("realWorld.purpose.currentEvents"),
      desc: t("realWorld.purpose.currentEventsDesc"),
    },
    {
      id: "Mesleki Yönelimi Desteklemek",
      emoji: "💼",
      label: t("realWorld.purpose.career"),
      desc: t("realWorld.purpose.careerDesc"),
    },
    {
      id: "Toplumsal Farkındalık ve Sorumluluk Geliştirmek",
      emoji: "🤝",
      label: t("realWorld.purpose.socialAwareness"),
      desc: t("realWorld.purpose.socialAwarenessDesc"),
    },
    {
      id: "Güncel ve Gündemde Olan Gelişmeler ile İlişki Kurmak",
      emoji: "📡",
      label: t("realWorld.purpose.trending"),
      desc: t("realWorld.purpose.trendingDesc"),
    },
  ];

  const handlePurposeToggle = (purposeId: string) => {
    const currentPurposes = data.usagePurposes || [];
    const isSelected = currentPurposes.includes(purposeId);

    if (isSelected) {
      const newPurposes = currentPurposes.filter((id) => id !== purposeId);
      onDataChange({ usagePurposes: newPurposes });
    } else {
      const newPurposes = [...currentPurposes, purposeId];
      onDataChange({ usagePurposes: newPurposes });
    }
  };

  const handleCustomPurposeToggle = () => {
    const currentPurposes = data.usagePurposes || [];
    const hasCustomPurpose = currentPurposes.some(
      (purpose) => !usagePurposeOptions.some((option) => option.id === purpose),
    );

    if (hasCustomPurpose || showCustomPurpose) {
      const newPurposes = currentPurposes.filter((purpose) =>
        usagePurposeOptions.some((option) => option.id === purpose),
      );
      onDataChange({ usagePurposes: newPurposes });
      setShowCustomPurpose(false);
    } else {
      setShowCustomPurpose(true);
    }
  };

  const handleCustomPurposeChange = (value: string) => {
    const currentPurposes = data.usagePurposes || [];
    const filteredPurposes = currentPurposes.filter((purpose) =>
      usagePurposeOptions.some((option) => option.id === purpose),
    );

    if (value.trim()) {
      onDataChange({ usagePurposes: [...filteredPurposes, value.trim()] });
    } else {
      onDataChange({ usagePurposes: filteredPurposes });
    }
  };

  const isPurposeSelected = (purposeId: string) => {
    return (data.usagePurposes || []).includes(purposeId);
  };

  const isCustomPurposeSelected = () => {
    const currentPurposes = data.usagePurposes || [];
    return (
      showCustomPurpose ||
      currentPurposes.some(
        (purpose) =>
          !usagePurposeOptions.some((option) => option.id === purpose),
      )
    );
  };

  const getCustomPurposeValue = () => {
    const currentPurposes = data.usagePurposes || [];
    const customPurpose = currentPurposes.find(
      (purpose) => !usagePurposeOptions.some((option) => option.id === purpose),
    );
    return customPurpose || "";
  };

  return (
    <div className="space-y-8">
      <div className="text-center mb-6">
        <h2
          className={`text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-${themeColor}-600 to-${themeColor}-800 dark:from-${themeColor}-400 dark:to-${themeColor}-600`}
        >
          {t("realWorld.steps.purpose.title")}
        </h2>
        <p className="text-gray-600 dark:text-gray-400 mt-2">
          {t("realWorld.steps.purpose.description")}
        </p>
      </div>

      <div>
        <h3
          className={`text-lg font-medium text-${themeColor}-700 dark:text-${themeColor}-300 mb-4`}
        >
          {t("realWorld.form.usagePurposes")}
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
          {t("realWorld.form.usagePurposesDesc")}
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
          {usagePurposeOptions.map((option) => (
            <div
              key={option.id}
              className={`p-4 rounded-lg border-2 cursor-pointer transition-all duration-200 ${
                isPurposeSelected(option.id)
                  ? `border-${themeColor}-500 bg-${themeColor}-50 dark:bg-${themeColor}-900/20`
                  : "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600"
              }`}
              onClick={() => handlePurposeToggle(option.id)}
            >
              <div className="flex items-start gap-3">
                <div
                  className={`w-5 h-5 rounded border-2 flex items-center justify-center mt-0.5 ${
                    isPurposeSelected(option.id)
                      ? `border-${themeColor}-500 bg-${themeColor}-500`
                      : "border-gray-300 dark:border-gray-600"
                  }`}
                >
                  {isPurposeSelected(option.id) && (
                    <div className="w-2.5 h-2.5 bg-white rounded-sm"></div>
                  )}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-lg">{option.emoji}</span>
                    <span className="font-medium text-gray-900 dark:text-gray-100">
                      {option.label}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {option.desc}
                  </p>
                </div>
              </div>
            </div>
          ))}

          {/* Custom Purpose Option */}
          <div
            className={`p-4 rounded-lg border-2 cursor-pointer transition-all duration-200 ${
              isCustomPurposeSelected()
                ? `border-${themeColor}-500 bg-${themeColor}-50 dark:bg-${themeColor}-900/20`
                : "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600"
            }`}
            onClick={handleCustomPurposeToggle}
          >
            <div className="flex items-start gap-3">
              <div
                className={`w-5 h-5 rounded border-2 flex items-center justify-center mt-0.5 ${
                  isCustomPurposeSelected()
                    ? `border-${themeColor}-500 bg-${themeColor}-500`
                    : "border-gray-300 dark:border-gray-600"
                }`}
              >
                {isCustomPurposeSelected() && (
                  <div className="w-2.5 h-2.5 bg-white rounded-sm"></div>
                )}
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-lg">✏️</span>
                  <span className="font-medium text-gray-900 dark:text-gray-100">
                    {t("realWorld.purpose.other")}
                  </span>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {t("realWorld.purpose.otherDesc")}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Custom Purpose Input */}
        {isCustomPurposeSelected() && (
          <div className="mt-4">
            <label
              className={`block text-sm font-medium text-${themeColor}-700 dark:text-${themeColor}-300 mb-2`}
            >
              {t("realWorld.form.customPurpose")}
            </label>
            <input
              type="text"
              value={getCustomPurposeValue()}
              onChange={(e) => handleCustomPurposeChange(e.target.value)}
              className={`w-full rounded-xl border-2 border-gray-200 dark:border-gray-700 p-4 text-base focus:ring-2 focus:ring-${themeColor}-500 focus:border-transparent dark:bg-gray-700/50 transition-all duration-200`}
              placeholder={t("realWorld.form.customPurposePlaceholder")}
            />
          </div>
        )}
      </div>
    </div>
  );
};
